import { FC, useState } from 'react';
import { classNames } from 'primereact/utils';
import { PencilIcon, ProviderAvatar } from '@repo/atomic-components';

interface DocumentProviderCellProps {
  rowId: string;
  providerName: string;
  providerType: string;
  onSelectProviderClick: (e: React.MouseEvent<HTMLButtonElement>, rowId: string) => void;
}

/**
 * Document-provider cell used by the medical-records list table and
 * the case-page uploaded-records table.
 *
 * Default: ProviderAvatar (or unassigned silhouette) + name + optional
 * type subtitle. Hovering swaps the avatar for a grey-circle pencil
 * chip — same affordance the V2 case-info sidebar uses for editable
 * rows. Clicking fires `onSelectProviderClick` so the consumer can
 * anchor a popup against the cell's bounding rect.
 */
export const DocumentProviderCell: FC<DocumentProviderCellProps> = ({
  rowId,
  providerName,
  providerType,
  onSelectProviderClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!providerName) {
    return (
      <button
        type="button"
        onClick={(e) => onSelectProviderClick(e, rowId)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="group flex cursor-pointer items-center gap-2"
      >
        {isHovered ? (
          <div className="bg-fletchers-gray-110 flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
            <PencilIcon className="text-fletchers-black-900 h-3 w-3" />
          </div>
        ) : (
          <ProviderAvatar unassigned size={24} />
        )}
        <span
          className={classNames(
            'italic transition-colors',
            isHovered ? 'text-fletchers-black-900' : 'text-fletchers-gray-200',
          )}
        >
          Select provider
        </span>
      </button>
    );
  }

  const displayName = providerName.length > 32 ? `${providerName.slice(0, 32)}...` : providerName;

  return (
    <button
      type="button"
      onClick={(e) => onSelectProviderClick(e, rowId)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group flex cursor-pointer items-center gap-2"
    >
      {isHovered ? (
        <div className="bg-fletchers-gray-110 flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
          <PencilIcon className="text-fletchers-black-900 h-3 w-3" />
        </div>
      ) : (
        <ProviderAvatar name={providerName} size={24} />
      )}
      <div className="flex flex-col items-start">
        <span
          className={classNames(
            'block max-w-[400px] overflow-hidden overflow-ellipsis whitespace-nowrap transition-colors',
            isHovered ? 'text-fletchers-black-900' : '',
          )}
          title={providerName}
        >
          {displayName}
        </span>
        {providerType && (
          <span className="text-fletchers-gray-250 text-[12px]/[16px] whitespace-nowrap">{providerType}</span>
        )}
      </div>
    </button>
  );
};
